import React from 'react'
import { useNavigate, useParams, useRouteContext } from '@tanstack/react-router'
import { Accordion, Button, CardGroup, Col, Container, Modal, Row } from 'react-bootstrap'
import { useQueryClient } from '@tanstack/react-query'

import Card from "@/components/shared/Card";
import { DetailCard } from '@/components/components.js'
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { CHLD } from '@/models/chld.tsx'
import { ENRL } from '@/models.ts'
import { CAPSDACTable, Filter, useChildTableColumns, useFamiTableColumns, useEnrlTableColumns } from '@/components/table'
import { useChildData, useCoreData, useCSPPIDMatches, useLeaData, useFileSubmissionState } from '@/queries.ts'
import { useAuthenticatedSuspenseQuery } from "@/queries/shared";
import { FileStatusEnum, FileSubmission, ValidationError } from '@/models/file-submission.tsx'
import { FileSubmissionStatus } from '@/pages/SubmissionsPage'
import { CSPPIDMatch, CSPPIDMatchLEA, CSPPIDMatchAction, MatchActionType, MatchLEAStatus, csppidMatchMutationFunctions } from '@/models/mid.tsx'
import LayoutContentArea from '@/components/layout/LayoutContentArea'

export default function MidDetailPage() {
  const leaContext = useRouteContext({ from: '/$leaid' })
  const params = useParams({
    from: "/$leaid/_error-wrapper/mid/$mid",
  });
  const leaid = params.leaid as string;
  const matchid = params.mid as string;

  // --- All hooks must run unconditionally, before any early return, to satisfy
  //     the Rules of Hooks. `match` may be undefined here; hooks that depend on
  //     it use null-safe args (mirroring MIDDetailPopup), and the early returns
  //     below run only AFTER every hook has been called.
  const matches = useCSPPIDMatches(leaid)
  const matchLEA = matches.data.find(x => x.CSPPIDMatchId == matchid)
  const match = matchLEA?.Match

  const { data, error } = useAuthenticatedSuspenseQuery<CSPPIDMatchAction[]>(`/json/csppid_match/${leaid}/${matchid}`, ["match", leaid, matchid])
  const coreData = useCoreData()
  const result = useLeaData(leaContext.currentUserOrg.esiIdPath)
  const primaryChild = useChildData(match?.CSPPID ?? "").data
  const matchedChild = useChildData(match?.MatchedCSPPID ?? "").data
  const leaData = result.data

  const chldColumns = useChildTableColumns(null, leaData, undefined)
  const famiColumns = useFamiTableColumns(null, undefined)
  const enrlColumns = useEnrlTableColumns(null, leaContext.currentOrgPreschools, undefined)
  //const mutateFns = csppidMatchMutationFunctions(leaContext.currentUserOrg.esiIdPath, setFileSubmission)
  const mutateFns = csppidMatchMutationFunctions()

  // --- Early returns now live AFTER all hooks ---
  // No match found for this id: nothing to render.
  if (typeof (match) === 'undefined') return <div />
  // Child data not loaded yet (useChildData().data is undefined until resolved).
  // Without this guard the spreads below would throw a TypeError.
  if (!primaryChild || !matchedChild) return <div />

  const columns: GridColumnProps[] = [
    { id: "Action", field: "Action", title: "Action" },
    { id: "UserID", field: "UserID", title: "User" },
    { id: "Status", field: "Status", title: "Status" },
    { id: "CreateDate", field: "CreateDate", title: "Create Date" },
  ];

  const mergedChild = {
    chld: [...primaryChild.chld, ...matchedChild.chld],
    fami: [...primaryChild.fami, ...matchedChild.fami],
    enrl: [...primaryChild.enrl, ...matchedChild.enrl],
  }
  const primaryENRL: ENRL = (primaryChild.enrl.length > 0) ? primaryChild.enrl[0] : {}
  const primaryCHLD: CHLD = (primaryChild.chld.length > 0) ? primaryChild.chld[0] : {
    CSPPID: matchLEA.CSPPID,
    PreschoolCode: mergedChild.chld.find(x => x.CSPPID === matchLEA.CSPPID)?.PreschoolCode,
  }

  mergedChild.chld.sort((x, y) => (y.EffectiveStartDate ?? '').localeCompare(x.EffectiveStartDate ?? ''))
  mergedChild.fami.sort((x, y) => (y.EffectiveStartDate ?? '').localeCompare(x.EffectiveStartDate ?? ''))
  mergedChild.enrl.sort((x, y) => (y.EnrollmentStartDate ?? '').localeCompare(x.EnrollmentStartDate ?? ''))
  return (
    <LayoutContentArea
      headingText="Multiple Identifiers Resolutions"
    >
      <CardGroup>
        <DetailCard key="1" items={[
          { name: 'Child Name', value: `${primaryCHLD.ChildFirstName} ${primaryCHLD.ChildLastName}` },
          { name: 'Child Birth Date', value: primaryCHLD.ChildDateOfBirth },
          { name: 'School of Attendance', value: primaryENRL.PreschoolCode },
        ]} />
        <DetailCard key="2" items={[
          { name: 'Local ID', value: primaryCHLD.LocalChildId },
          { name: 'Gender', value: primaryCHLD.ChildGender },
        ]} />
        <DetailCard key="3" items={[
          { name: 'CSPPID', value: primaryCHLD.CSPPID },
          { name: 'Enrollment Start Date', value: primaryENRL.EnrollmentStartDate },
        ]} />
      </CardGroup>
      <Card>
        {/*{mutateFns.mutationState.isPending &&*/}
        {/*  <div id="divAddOrEditClassroomDialogLoading">*/}
        {/*    <i className="fa fa-lg fa-spinner fa-spin me-2"></i>*/}
        {/*  </div>*/}
        {/*}*/}
        {(matchLEA.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA || matchLEA.Status === MatchLEAStatus.UNRESOLVED_INACTIVE_LEA) &&
          <Button className="btn btn-secondary" variant="secondary" onClick={() => mutateFns.handleSave( match.Id, MatchActionType.REJECT )}>Records Should Not Be Merged</Button>
        }
        {(matchLEA.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA || matchLEA.Status === MatchLEAStatus.UNRESOLVED_INACTIVE_LEA || matchLEA.Status === MatchLEAStatus.RESOLVED_REJECTED) &&
          <Button className="btn btn-primary" variant="primary" onClick={() => mutateFns.handleSave( match.Id, MatchActionType.MERGE )}>Merge Records</Button>
        }
      {/*  {matchLEA.Status === MatchActionEnum.RESOLVED_MERGED &&*/}
      {/*    <Button className="btn btn-primary" variant="primary" onClick={() => mutateFns.handleSave({ ...csppid_match, ...{ Action: MatchActionEnum.Unmerge } })}>Unmerge Records</Button>*/}
      {/*  }*/}
      </Card>
      <Card className="mb-3 no-padding card-border">
        <KendoTableClientState
          data={data}
          columns={columns}
          error={error}
        />
      </Card>
    </LayoutContentArea>
  );
}